import { ScrollText } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';
import { LayoutWrapper } from '../components/LayoutWrapper';

export function TermsAndConditions() {
  const { formatMessage } = useIntl();

  return (
    <LayoutWrapper hasHero>
      {/* Hero Section */}
      <div className='bg-blue-600 text-white py-16'>
        <div className='container mx-auto px-4 text-center'>
          <ScrollText className='w-16 h-16 mx-auto mb-6' />
          <h1 className='text-4xl font-bold mb-4'>
            <FormattedMessage
              id='terms.title'
              defaultMessage='Условия использования'
            />
          </h1>
          <p className='text-xl text-blue-100 max-w-2xl mx-auto'>
            <FormattedMessage
              id='terms.subtitle'
              defaultMessage='Пожалуйста, внимательно ознакомьтесь с условиями использования сервиса'
            />
          </p>
        </div>
      </div>

      {/* Content */}
      <div className='container mx-auto px-4 py-12'>
        <div className='max-w-3xl mx-auto'>
          <div className='max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg'>
            <h1 className='text-3xl font-bold mb-6 text-gray-800'>
              <FormattedMessage
                id='rules.title'
                defaultMessage='Правила использования сервиса NovoService'
              />
            </h1>

            {/* Содержание */}
            <section className='mb-8'>
              <h2 className='text-xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.contents.title'
                  defaultMessage='Содержание'
                />
              </h2>
              <ul className='list-disc pl-6 text-gray-600'>
                <li>
                  <a href='#section1' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item1'
                      defaultMessage='Термины и определения'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section2' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item2'
                      defaultMessage='Общие положения'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section3' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item3'
                      defaultMessage='Учетная запись'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section4' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item4'
                      defaultMessage='Правила размещения услуг'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section5' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item5'
                      defaultMessage='Незаконные действия и нарушения правил'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section6' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item6'
                      defaultMessage='Порядок подачи жалоб'
                    />
                  </a>
                </li>
                <li>
                  <a href='#section7' className='text-blue-600 hover:underline'>
                    <FormattedMessage
                      id='rules.contents.item7'
                      defaultMessage='Заключительные положения'
                    />
                  </a>
                </li>
              </ul>
            </section>

            {/* Приложения */}
            <section className='mb-8'>
              <h2 className='text-xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.appendices.title'
                  defaultMessage='Приложения'
                />
              </h2>
              <ul className='list-disc pl-6 text-gray-600'>
                <li>
                  <a
                    href='#appendix1'
                    className='text-blue-600 hover:underline'
                  >
                    <FormattedMessage
                      id='rules.appendices.item1'
                      defaultMessage='Приложение 1: Запрещенные и условно разрешенные услуги'
                    />
                  </a>
                </li>
              </ul>
            </section>

            {/* 1. Термины и определения */}
            <section id='section1' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section1.title'
                  defaultMessage='1. Термины и определения'
                />
              </h2>
              <dl className='space-y-2 text-gray-600'>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term1'
                    defaultMessage='Гость'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc1'
                    defaultMessage='Человек, который использует веб-сайт, не имея учетной записи или не войдя в нее.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term2'
                    defaultMessage='Категория'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc2'
                    defaultMessage='Тематический раздел (например, «Юридические услуги» или «Образование»), к которому относится размещаемая услуга. Категории могут включать связанные подкатегории.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term3'
                    defaultMessage='Клиент'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc3'
                    defaultMessage='Пользователь, который ищет услуги через веб-сайт для личных нужд, не связанных с профессиональной или коммерческой деятельностью.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term4'
                    defaultMessage='NovoService'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc4'
                    defaultMessage='Онлайн-платформа, доступная на домене novoservice.ro, созданная для помощи эмигрантам из стран СНГ в Румынии в поиске и предоставлении услуг.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term5'
                    defaultMessage='Учетная запись'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc5'
                    defaultMessage='Набор данных о конкретном пользователе, включая информацию о его действиях на веб-сайте и предоставленные им сведения. Подробности указаны в разделе 3.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term6'
                    defaultMessage='Пользователь'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc6'
                    defaultMessage='Физическое лицо, юридическое лицо или подразделение, которое использует веб-сайт после входа в учетную запись.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term7'
                    defaultMessage='Поставщик услуг'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc7'
                    defaultMessage='Пользователь, который размещает информацию о своих услугах на веб-сайте.'
                  />
                </dd>
                <dt className='font-medium'>
                  <FormattedMessage
                    id='rules.section1.term8'
                    defaultMessage='Услуга'
                  />
                </dt>
                <dd>
                  <FormattedMessage
                    id='rules.section1.desc8'
                    defaultMessage='Предложение, размещенное поставщиком услуг на веб-сайте, доступное для просмотра и использования клиентами.'
                  />
                </dd>
              </dl>
            </section>

            {/* 2. Общие положения */}
            <section id='section2' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section2.title'
                  defaultMessage='2. Общие положения'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section2.item1'
                    defaultMessage='Правила использования веб-сайта NovoService, включая регистрацию и размещение услуг, изложены в настоящем документе. Каждый, кто использует веб-сайт, обязан ознакомиться с этими правилами.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item2'
                    defaultMessage='Гости могут просматривать контент веб-сайта с ограниченным доступом к функциям, в соответствии с законом и принципами добросовестности.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item3'
                    defaultMessage='Контент на веб-сайте, включая описания услуг (текст, изображения и другие материалы), защищен правами интеллектуальной собственности NovoService, поставщиков услуг или третьих лиц. Без письменного согласия правообладателя использование этого контента запрещено. Также запрещено копирование, обработка или передача данных с веб-сайта для использования на других ресурсах или вне интернета без согласия NovoService.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item4'
                    defaultMessage='NovoService предоставляет платформу для размещения и поиска услуг, но не является стороной соглашений между пользователями.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item5'
                    defaultMessage='На веб-сайте доступны следующие возможности:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item5.sub1'
                        defaultMessage='просмотр контента;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item5.sub2'
                        defaultMessage='создание и использование учетной записи;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item5.sub3'
                        defaultMessage='размещение информации об услугах;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item5.sub4'
                        defaultMessage='поиск услуг другими пользователями.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item6'
                    defaultMessage='Для полноценного использования веб-сайта устройство пользователя должно соответствовать следующим требованиям:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item6.sub1'
                        defaultMessage='стабильное интернет-соединение с поддержкой HTTPS;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item6.sub2'
                        defaultMessage='современный браузер (например, Google Chrome, Mozilla Firefox) с поддержкой HTML5 и CSS3;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section2.item6.sub3'
                        defaultMessage='включенная поддержка JavaScript и cookies.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section2.item7'
                    defaultMessage='NovoService стремится обеспечить стабильную работу веб-сайта, но оставляет за собой право временно приостанавливать его функционирование для технических работ, о чем пользователи будут уведомлены заранее, если это возможно.'
                  />
                </li>
              </ol>
            </section>

            {/* 3. Учетная запись */}
            <section id='section3' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section3.title'
                  defaultMessage='3. Учетная запись'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section3.item1'
                    defaultMessage='Для размещения услуг или полноценного использования функций веб-сайта гость должен зарегистрировать учетную запись и стать пользователем. Учетная запись позволяет:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item1.sub1'
                        defaultMessage='размещать и управлять информацией об услугах;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item1.sub2'
                        defaultMessage='просматривать услуги других пользователей.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item2'
                    defaultMessage='Пользователем может быть физическое лицо с достаточной правоспособностью, юридическое лицо или его подразделение. Для юридических лиц учетную запись создает уполномоченное лицо.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item3'
                    defaultMessage='Каждый пользователь может иметь только одну учетную запись, за исключением случаев:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item3.sub1'
                        defaultMessage='использования одной учетной записи для личных целей и другой — для профессиональной деятельности;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item3.sub2'
                        defaultMessage='утраты доступа к учетной записи (например, из-за забытого пароля).'
                      />
                    </li>
                  </ul>
                  <FormattedMessage
                    id='rules.section3.item3.cont'
                    defaultMessage='NovoService может проверить такие случаи и приостановить действие учетной записи до выяснения обстоятельств.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item4'
                    defaultMessage='Для регистрации необходимо:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item4.sub1'
                        defaultMessage='заполнить форму на веб-сайте, указав адрес электронной почты и уникальный пароль;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item4.sub2'
                        defaultMessage='принять настоящие правила.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item5'
                    defaultMessage='После регистрации на указанный email придет подтверждение для активации учетной записи. Активация завершает процесс регистрации.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item6'
                    defaultMessage='Пользователь обязан предоставлять точные и актуальные данные при регистрации и обновлять их при необходимости. NovoService может заблокировать учетную запись, если данные окажутся недостоверными.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item7'
                    defaultMessage='Пользователь должен сохранять конфиденциальность данных для входа в учетную запись и сообщать NovoService о любом несанкционированном доступе.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item8'
                    defaultMessage='Учетная запись создается на неопределенный срок. Пользователь может удалить ее через настройки или обратившись в службу поддержки. После удаления доступ к данным учетной записи теряется.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section3.item9'
                    defaultMessage='NovoService может прекратить действие учетной записи, если:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item9.sub1'
                        defaultMessage='пользователь не входил в нее более 24 месяцев (с уведомлением за 30 дней);'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section3.item9.sub2'
                        defaultMessage='пользователь нарушает правила, продолжая это делать после предупреждения.'
                      />
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            {/* 4. Правила размещения услуг */}
            <section id='section4' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section4.title'
                  defaultMessage='4. Правила размещения услуг'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section4.item1'
                    defaultMessage='Пользователь может размещать информацию об услугах на веб-сайте после заполнения соответствующей формы и подтверждения учетной записи.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item2'
                    defaultMessage='Размещенная информация доступна всем пользователям и гостям веб-сайта.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item3'
                    defaultMessage='При размещении услуги пользователь предоставляет NovoService неисключительное право использовать ее описание (текст, изображения) для отображения на веб-сайте и его продвижения.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item4'
                    defaultMessage='Описание услуги должно:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item4.sub1'
                        defaultMessage='быть составлено на русском, украинском или английском языке, без грубых или оскорбительных выражений;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item4.sub2'
                        defaultMessage='относиться к одной категории или подкатегории, соответствующей теме услуги;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item4.sub3'
                        defaultMessage='содержать точное, понятное и полное описание услуги без введения в заблуждение;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item4.sub4'
                        defaultMessage='указывать, предоставляет ли пользователь услугу лично или рекламирует ее от имени другого лица;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item4.sub5'
                        defaultMessage='предлагать только одну услугу в одном объявлении.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item5'
                    defaultMessage='Услуга не может быть:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item5.sub1'
                        defaultMessage='поиском услуг (кроме случаев, предусмотренных категориями);'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item5.sub2'
                        defaultMessage='предложением личного, романтического или сексуального характера;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section4.item5.sub3'
                        defaultMessage='запрещенной услугой согласно Приложению 1 или действующему законодательству Румынии.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item6'
                    defaultMessage='Размещение услуги начинается сразу после ее утверждения нашими администраторами в течении 24 часов и длится неопределенный срок, если пользователь не удалит ее раньше.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section4.item7'
                    defaultMessage='NovoService может переместить услугу в другую категорию, если она размещена некорректно, или удалить ее в случае нарушения правил.'
                  />
                </li>
              </ol>
            </section>

            {/* 5. Незаконные действия и нарушения правил */}
            <section id='section5' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section5.title'
                  defaultMessage='5. Незаконные действия и нарушения правил'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section5.item1'
                    defaultMessage='Пользователь обязан использовать веб-сайт в соответствии с его назначением, соблюдая законы Румынии и уважая права других лиц. Запрещается:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item1.sub1'
                        defaultMessage='мешать работе веб-сайта или вмешиваться в его содержимое;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item1.sub2'
                        defaultMessage='размещать незаконный контент (например, нарушающий авторские права, дискриминационный или запрещенный законом);'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item1.sub3'
                        defaultMessage='вводить в заблуждение других пользователей или NovoService;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item1.sub4'
                        defaultMessage='использовать веб-сайт недобросовестно или вопреки его целям.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section5.item2'
                    defaultMessage='NovoService не проверяет весь контент автоматически, но может удалить или заблокировать информацию, нарушающую правила, после получения жалобы или по собственной инициативе.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section5.item3'
                    defaultMessage='Любой пользователь или гость может сообщить о нарушении через форму обратной связи, указав детали (например, ID услуги и причину жалобы).'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section5.item4'
                    defaultMessage='При выявлении нарушений NovoService может:'
                  />
                  <ul className='list-disc pl-6 mt-1'>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item4.sub1'
                        defaultMessage='заблокировать или удалить контент;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item4.sub2'
                        defaultMessage='провести проверку обстоятельств;'
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id='rules.section5.item4.sub3'
                        defaultMessage='уведомить пользователя о причинах блокировки.'
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section5.item5'
                    defaultMessage='Пользователь может оспорить блокировку, обратившись в службу поддержки. Если жалоба обоснована, контент будет восстановлен.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section5.item6'
                    defaultMessage='В случае серьезных или повторных нарушений NovoService может заблокировать учетную запись пользователя временно или навсегда.'
                  />
                </li>
              </ol>
            </section>

            {/* 6. Порядок подачи жалоб */}
            <section id='section6' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section6.title'
                  defaultMessage='6. Порядок подачи жалоб'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section6.item1'
                    defaultMessage='При сбоях в работе веб-сайта или других проблемах пользователь может обратиться в службу поддержки через форму на сайте, указав детали проблемы.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section6.item2'
                    defaultMessage='Жалоба должна содержать: имя пользователя, email или номер телефона, связанный с учетной записью, описание ситуации и запрос.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section6.item3'
                    defaultMessage='NovoService рассмотрит жалобу в течение 15 дней с момента получения всех необходимых данных и ответит пользователю.'
                  />
                </li>
              </ol>
            </section>

            {/* 7. Заключительные положения */}
            <section id='section7' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.section7.title'
                  defaultMessage='7. Заключительные положения'
                />
              </h2>
              <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                <li>
                  <FormattedMessage
                    id='rules.section7.item1'
                    defaultMessage='NovoService может вносить изменения в правила, уведомляя пользователей через веб-сайт за 15 дней до вступления изменений в силу.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section7.item2'
                    defaultMessage='Пользователь, не согласный с изменениями, может удалить учетную запись.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section7.item3'
                    defaultMessage='Действие правил регулируется законодательством Румынии, если иное не предусмотрено законом.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section7.item4'
                    defaultMessage='Споры, связанные с использованием веб-сайта, рассматриваются судами общей юрисдикции Румынии.'
                  />
                </li>
                <li>
                  <FormattedMessage
                    id='rules.section7.item5'
                    defaultMessage='Приложения являются неотъемлемой частью этих правил.'
                  />
                </li>
              </ol>
            </section>

            {/* Приложение 1 */}
            <section id='appendix1' className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                <FormattedMessage
                  id='rules.appendix1.title'
                  defaultMessage='Приложение 1: Запрещенные и условно разрешенные услуги'
                />
              </h2>

              {/* 1. Запрещенные услуги */}
              <section className='mb-8'>
                <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                  <FormattedMessage
                    id='appendix5.section1.title'
                    defaultMessage='1. Запрещенные услуги'
                  />
                </h2>
                <p className='text-gray-600 mb-4'>
                  <FormattedMessage
                    id='appendix5.section1.intro'
                    defaultMessage='Пользователю запрещается размещать объявления, касающиеся предоставления следующих услуг (Запрещенные услуги):'
                  />
                </p>
                <ol className='list-decimal pl-6 text-gray-600 space-y-2'>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item3'
                      defaultMessage='Секс-услуги, проституция, любые услуги интимного характера, виды массажа: эротический, боди, лингама, тантрический и т.д.; услуги свингер-клубов; эскорт-услуги, стриптиз и стриптиз-шоу, эротические танцы и подобные;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item4'
                      defaultMessage='Предложения знакомств, дружбы, легких отношений, а также услуги по администрированию сайтов знакомств;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item15'
                      defaultMessage='Услуги, предусматривающие консультирование, посредничество, помощь и т.д. в вопросах въезда на территорию проведения Операции Объединенных сил и временно оккупированные территории;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item35'
                      defaultMessage='Услуги по написанию диссертаций, дипломов, бакалаврских и магистерских работ, университетских и дипломных работ, рефератов и других подобных работ, а также шпаргалок;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item36'
                      defaultMessage='Услуги, связанные с участием в так называемых финансовых пирамидах, то есть финансовых структурах, созданных за счет привлечения новых участников, где основным (или единственным) источником прибыли является вступительный взнос, уплачиваемый ими;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item37'
                      defaultMessage='Услуги, связанные с предоставлением помощи при входе в системы нелегальных лотерей и ставок;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item48'
                      defaultMessage='Услуги ручного и программного размещения, рассылки объявлений на любых ресурсах, а также вакансии на подобные позиции;'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item49'
                      defaultMessage='Предложения работы:'
                    />
                    <ul className='list-disc pl-6 mt-1'>
                      <li>
                        <FormattedMessage
                          id='appendix5.section1.item49.sub1'
                          defaultMessage='участие в партнерских системах (например, работа с переходами по ссылкам);'
                        />
                      </li>
                      <li>
                        <FormattedMessage
                          id='appendix5.section1.item49.sub2'
                          defaultMessage='с обязательной предоплатой;'
                        />
                      </li>
                      <li>
                        <FormattedMessage
                          id='appendix5.section1.item49.sub3'
                          defaultMessage='в сети Интернет без указания физического адреса и прямых контактов работодателя;'
                        />
                      </li>
                      <li>
                        <FormattedMessage
                          id='appendix5.section1.item49.sub4'
                          defaultMessage='в ночных клубах за границей; веб-моделями; переводчики/переводчицы в брачные агентства; танцовщицы и участие в шоу-программах и подобные;'
                        />
                      </li>
                    </ul>
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item56'
                      defaultMessage='Услуги, связанные с оккультной тематикой (привороты, заговоры, гадания, сатанизм, ритуалы, колдовство, ведьмы, магия, целительство, экстрасенсы и т.д.);'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='appendix5.section1.item67'
                      defaultMessage='Услуги по увеличению количества подписчиков/охвата страниц в социальных сетях;'
                    />
                  </li>
                </ol>
              </section>

              {/* 2. Условно разрешенные услуги */}
              <section className='mb-8'>
                <h2 className='text-2xl font-semibold mb-4 text-gray-700'>
                  <FormattedMessage
                    id='appendix5.section2.title'
                    defaultMessage='2. Условно разрешенные услуги'
                  />
                </h2>
                <p className='text-gray-600 mb-4'>
                  <FormattedMessage
                    id='appendix5.section2.intro'
                    defaultMessage='Размещение следующих услуг может быть разрешено в объявлениях при условии, что они соответствуют указанным ниже требованиям, а описание услуги в объявлении содержит указанный контент (условно разрешенные услуги):'
                  />
                </p>
                <table className='w-full text-gray-600 border-collapse'>
                  <thead>
                    <tr className='bg-gray-100'>
                      <th className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.header1'
                          defaultMessage='№'
                        />
                      </th>
                      <th className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.header2'
                          defaultMessage='Тип услуги'
                        />
                      </th>
                      <th className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.header3'
                          defaultMessage='Условия'
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='p-2 border-b'>1.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item4.type'
                          defaultMessage='Предложение вакансий от кадровых агентств'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item4.condition'
                          defaultMessage='Пользователь обязан указать, что услуга бесплатна для соискателей.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>2.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item5.type'
                          defaultMessage='Работа для подростков 14-16 лет'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item5.condition'
                          defaultMessage='Пользователь обязан прямо указать в описании объявления о наличии разрешения родителей/опекунов на работу.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>3.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item6.type'
                          defaultMessage='Работа за границей'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item6.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления наименование юридического лица или субъекта предпринимательства, предоставляющего услугу.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>4.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item7.type'
                          defaultMessage='Услуги кредитования'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item7.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления следующую информацию: ● Наименование банка или иной финансовой организации; ● Лицензию банка или иной финансовой организации, выдающей кредит; ● Общие требования к заемщику.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>5.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item8.type'
                          defaultMessage='Услуги ломбардов'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item8.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления наименование и адрес субъекта, предоставляющего услуги.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>6.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item9.type'
                          defaultMessage='Финансовый, оперативный лизинг'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item9.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления реквизиты лицензии на осуществление указанной деятельности или прикрепить скан-копию такого документа.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>7.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item10.type'
                          defaultMessage='Услуги прописки'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item10.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления наименование юридической фирмы, агентства и т.д.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>8.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item11.type'
                          defaultMessage='Помощь в получении/замене водительского удостоверения, регистрация авто'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item11.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления наименование юридического лица или субъекта предпринимательства, предоставляющего услугу.'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='p-2 border-b'>9.</td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item12.type'
                          defaultMessage='Проверка на прослушивание, поиск жучков'
                        />
                      </td>
                      <td className='p-2 border-b'>
                        <FormattedMessage
                          id='appendix5.section2.table.item12.condition'
                          defaultMessage='Пользователь обязан указать в описании объявления реквизиты лицензии на указанный вид деятельности.'
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className='text-gray-600 mt-4'>
                  <FormattedMessage
                    id='appendix5.section2.footer'
                    defaultMessage='Обязательство сделать соответствующее заявление согласно таблице, приведенной выше, должно быть выполнено в описании объявления, если иное не указано в таблице.'
                  />
                </p>
              </section>
            </section>
          </div>

          {/* Contact Section */}
          <div className='bg-blue-50 rounded-xl p-6 mt-8'>
            <h2 className='text-xl font-semibold mb-4'>
              <FormattedMessage
                id='terms.questionsTitle'
                defaultMessage='Остались вопросы?'
              />
            </h2>
            <p className='text-gray-600 mb-4'>
              <FormattedMessage
                id='terms.questionsDescription'
                defaultMessage='Если у вас есть вопросы об условиях использования сервиса, пожалуйста, свяжитесь с нами.'
              />
            </p>
            <Link to={ROUTES.contact}>
              <button className='bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700'>
                {formatMessage({
                  id: 'faq.contactButton',
                  defaultMessage: 'Связаться с поддержкой',
                })}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
}
